/**
 * Perplexicon branding and default expiration/limit constants.
 *
 * This is the single source of truth referenced by CLAUDE.md §9 and
 * docs/APP_PLAN.md §1.1: no other package or app may hardcode these values.
 * Import from here instead. Environment-specific overrides (e.g. a
 * configurable `SESSION_TTL_DAYS`) still read their *default* from this
 * package so there is exactly one place these numbers are defined.
 *
 * This package must remain platform-neutral: no React DOM, no Fastify, no
 * Node-only APIs (see TECH_STACK.md §89 and CLAUDE.md §4).
 */

/** Product branding. */
export const BRANDING = {
  /** Canonical product name, used in page titles, emails, and the OpenAPI spec. */
  appName: 'Perplexicon',
  /** Short name for PWA home-screen icons / manifest `short_name`. */
  pwaShortName: 'Perplexicon',
  /** Full PWA manifest `name`. */
  pwaName: 'Perplexicon — Multiplayer Game Platform',
  /** Human-readable product description, used in the PWA manifest, the OpenAPI info, and the public entry pages. */
  description: 'A growing collection of word games, puzzles, and peculiar challenges.',
  /** Short product tagline for public-facing footers and marketing surfaces. */
  tagline: 'There is always another puzzle waiting.',
  /** Display name used in the "From" header of outgoing transactional email. */
  mailFromName: 'Perplexicon',
  /** Title used when generating the OpenAPI specification. */
  openApiTitle: 'Perplexicon API',
} as const;

/**
 * Default expirations and limits.
 *
 * These are defaults only. Runtime configuration (apps/api/src/config) may
 * allow environment-specific overrides, but the *default value* always
 * originates here (APP_SPEC §2.1, docs/APP_PLAN.md §7.2–§7.7).
 */
export const DEFAULT_EXPIRATIONS = {
  /** Auth session sliding expiration, in days. Renewed on activity. */
  sessionTtlDays: 30,
  /** Password reset token lifetime, in minutes. Single-use. */
  passwordResetTtlMinutes: 60,
  /** Game invitation lifetime, in days. Enforced by sweeper and lazily on read. */
  invitationTtlDays: 7,
  /** Idempotency key record lifetime, in hours. */
  idempotencyKeyTtlHours: 24,
} as const;

/**
 * Default tunables for the *global backstop* rate limiter
 * (`apps/api/src/plugins/rate-limit.ts`) — not the abuse-sensitive
 * per-route policies below (`AUTH_RATE_LIMITS`, `SOCIAL_RATE_LIMITS`,
 * `GAME_RATE_LIMITS`), which are keyed and sized independently and are
 * unaffected by this constant.
 *
 * `maxRequests` derivation (TECH_STACK §53 — "do not make normal game
 * interactions unnecessarily restrictive"), recalculated for the
 * session-status-aware polling introduced by
 * `docs/games/word-rush/GAME_PLAN.md` §13.7 (2000ms while a session is
 * `active`, 3000ms otherwise, stopped once `completed`): measured
 * legitimate one-tab worst case per 15-minute window is session polling at
 * 2s ≈ 450, the active-sessions list every 10s ≈ 90, incoming + outgoing
 * invitations every 10s each ≈ 180, unread-notification count every 30s ≈
 * 30 — roughly 750 total. The limiter is keyed per authenticated user
 * (falling back to IP only for anonymous requests — see `rate-limit.ts`),
 * so this budget is no longer shared across everyone behind one NAT.
 * `maxRequests` is set to roughly 3x that measured figure (the same
 * "≈3× measured legitimate load" rationale as before) so a user with a
 * couple of extra browser tabs/windows open on the same account, plus
 * normal manual actions (clicking through screens, occasional retries),
 * never approaches the limit during ordinary play — while a single
 * identity issuing thousands of requests in a window (a runaway client or
 * a scripted abuser) still gets stopped, which remains this limiter's
 * actual job.
 */
export const DEFAULT_RATE_LIMITS = {
  /** Default maximum requests per window for the global limiter, per identity (user, or IP if anonymous). */
  maxRequests: 3000,
  /** Default rate-limit window, in minutes. */
  windowMinutes: 15,
} as const;

/**
 * Per-route rate-limit policy for the specific abuse-sensitive
 * authentication endpoints named in docs/APP_PLAN.md §9.5 ("Indicative
 * defaults (all configurable via environment)"). Every route-specific
 * limit lives here — never inline in a route file — so it stays in one
 * place and can be overridden per environment later without touching
 * route code.
 */
export const AUTH_RATE_LIMITS = {
  /** Login attempts, keyed by IP + normalized username. */
  login: { max: 10, windowMinutes: 15 },
  /** Registrations, keyed by IP. */
  register: { max: 5, windowMinutes: 60 },
  /** Password-reset requests, keyed by IP. */
  passwordResetRequestPerIp: { max: 5, windowMinutes: 60 },
  /** Password-reset requests, keyed by the (normalized) target email address. */
  passwordResetRequestPerEmail: { max: 3, windowMinutes: 60 },
} as const;

/**
 * Per-route rate-limit policy for the abuse-sensitive social endpoints
 * named in docs/APP_PLAN.md §9.5 ("player search 30/min per user; friend
 * requests 30/hour per user"). Same rationale as `AUTH_RATE_LIMITS`: one
 * place, never inline in a route file.
 */
export const SOCIAL_RATE_LIMITS = {
  /** Player search requests, keyed by the authenticated user. */
  search: { max: 30, windowMinutes: 1 },
  /** Outgoing friend requests, keyed by the authenticated user. */
  friendRequestSend: { max: 30, windowMinutes: 60 },
} as const;

/**
 * Per-route rate-limit policy for game-lobby endpoints
 * (docs/games/word-rush/GAME_PLAN.md §13.2, §13.7). Join-code redemption is
 * the abuse-sensitive one: codes are 6 characters from a 30-symbol
 * unambiguous alphabet (≈7.3 × 10⁸ combinations), so 20 attempts per 5
 * minutes per user makes brute-force enumeration impractical while never
 * bothering a legitimate user typing in (and occasionally mistyping) a
 * code shared by a friend.
 */
export const GAME_RATE_LIMITS = {
  joinByCode: { max: 20, windowMinutes: 5 },
} as const;

/**
 * How often the background sweeper (`apps/api/src/services/sweeper.ts`)
 * re-runs its periodic tasks (expired invitations, due time-advancement,
 * idempotency-key pruning). Dropped from 15 minutes to 1 minute by
 * docs/games/word-rush/GAME_PLAN.md §13.3 once the sweeper also advances
 * time-driven session state (`advanceDueSessions`) — a due round timer or
 * lobby expiry should not sit unresolved in Active Games for up to a
 * quarter of an hour. Both sweep queries are cheap and indexed, so a 1
 * minute cadence carries no meaningful load.
 */
export const SWEEPER_CONFIG = {
  intervalMinutes: 1,
} as const;

/** Username length constraints, mirrored by the database CHECK constraint. */
export const USERNAME_CONSTRAINTS = {
  minLength: 3,
  maxLength: 24,
} as const;

/**
 * The opt-in, ephemeral, in-memory SSE real-time channel
 * (docs/games/word-delve/GAME_PLAN.md §15.1): `GET
 * /api/game-sessions/:id/stream` and `POST /api/game-sessions/:id/presence`
 * only exist for a game declaring `metadata.realtime` — see
 * `packages/game-engine/src/types.ts`. Single-process, in-memory only (no
 * Redis/pub-sub, TECH_STACK §86); these caps bound one API process's
 * exposure regardless of how many games opt in.
 */
export const REALTIME_LIMITS = {
  /** Maximum concurrent SSE subscribers for one session. */
  subscribersPerSession: 8,
  /** Maximum concurrent SSE subscribers across the whole process. */
  subscribersPerProcess: 500,
  /** How often an idle SSE connection receives a heartbeat comment, in seconds. */
  heartbeatSeconds: 15,
  /** How old a presence frame may be before a client should discard it as stale. */
  presenceMaxAgeMs: 3000,
} as const;

/**
 * Per-route rate-limit policy for `POST /api/game-sessions/:id/presence`
 * (docs/games/word-delve/GAME_PLAN.md §15.1) — deliberately its own budget,
 * replacing (not sharing) the global rate limiter for this one route: a
 * legitimate player's client throttles presence posts to roughly 10Hz per
 * the game plan, so 600/minute gives ample headroom for normal play while
 * still bounding a single identity's worst case.
 */
export const REALTIME_RATE_LIMITS = {
  presence: { max: 600, windowMinutes: 1 },
} as const;

/**
 * CSRF defense (docs/APP_PLAN.md §9.4, TECH_STACK §55): the name of the
 * custom request header every mutating request must carry. A cross-origin
 * simple HTML form cannot set a custom header without triggering a CORS
 * preflight, which fails because the API does not enable CORS in
 * production. `apps/api/src/plugins/security.ts` checks for its
 * *presence*; `packages/api-client` is the single place that *sends* it,
 * so the header name is defined exactly once here and imported by both.
 */
export const CSRF_CLIENT_HEADER_NAME = 'x-perplexicon-client';

/**
 * The rolling Game History window (APP_SPEC §44): the maximum number of
 * *completed* sessions the history surface ever returns, newest first.
 *
 * This is a retrieval limit, not a retention policy — nothing deletes older
 * `game_sessions` rows, they simply stop appearing in the history list once
 * 100 newer completed sessions exist. Enforced in the database query
 * (`modules/sessions/repository.ts`), never by truncating a larger result
 * client-side, so neither the API nor the browser ever carries a user's
 * entire historical game collection.
 */
export const GAME_HISTORY_LIMIT = 100;

/**
 * Retention defaults for security-oriented login/session metadata.
 *
 * The long-lived record on `auth_sessions` never stores a raw client IP —
 * it stores `ip_hmac`, an HMAC-SHA256 of the normalized address keyed by a
 * server-side secret (`IP_HMAC_SECRET`), which is enough to recognize that
 * two logins came from the same address without retaining the address
 * itself. A *raw* address is only ever written to the separate,
 * never-client-readable `security_ip_events` table, and only when an
 * operator explicitly opts in by setting a non-zero
 * `SECURITY_IP_RETENTION_DAYS`; the sweeper purges those rows once they
 * pass the window.
 *
 * The default is deliberately `0` — store nothing raw — because the
 * platform has no server-side security function that reads raw addresses
 * today, and retaining data with no consumer is exactly the sensitive-data
 * accumulation this policy exists to prevent.
 */
export const SECURITY_METADATA = {
  /** Default raw-IP retention in days. `0` disables raw-IP storage entirely. */
  rawIpRetentionDays: 0,
  /** The longest raw-IP retention window the configuration will accept. */
  maxRawIpRetentionDays: 30,
} as const;
